import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./modal.scss";

type Size = "sm" | "md" | "lg" | "xl";

interface GenericModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: Size;
  closable?: boolean;
  className?: string;
}

const sizeMap: Record<Size, string> = {
  sm: "360px",
  md: "540px",
  lg: "820px",
  xl: "1140px",
};

const Modal: React.FC<GenericModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  closable = true,
  className = "",
}) => {
  const elRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!elRef.current) {
      elRef.current = document.createElement("div");
    }
    const el = elRef.current;
    document.body.appendChild(el);
    return () => {
      if (el && document.body.contains(el)) document.body.removeChild(el);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !elRef.current) return null;
  const hasHeader = Boolean(title) || Boolean(description);

  const content = (
    <div className="generic-modal-backdrop" onMouseDown={onClose}>
      <div
        className={`generic-modal ${className} ${
          hasHeader ? "" : "generic-modal--no-header"
        }`}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ maxWidth: sizeMap[size] }}
        role="dialog"
        aria-modal="true"
      >
        {hasHeader && (
          <div className="generic-modal-header">
            <div className="generic-modal-header-content">
              <div className="generic-modal-title">{title}</div>
              {description && (
                <p className="generic-modal-description">{description}</p>
              )}
            </div>
            {closable && (
              <button
                aria-label="Close"
                className="generic-modal-close"
                onClick={onClose}
                type="button"
              >
                ×
              </button>
            )}
          </div>
        )}

        {!hasHeader && closable && (
          <button
            aria-label="Close"
            className="generic-modal-close no-header-close"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        )}

        <div className="generic-modal-body">{children}</div>
        {footer && <div className="generic-modal-footer">{footer}</div>}
      </div>
    </div>
  );

  return createPortal(content, elRef.current);
};

export default Modal;
